import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Détecte les erreurs typiques de "chunk obsolète après nouveau déploiement".
 * Quand Lovable/Vercel re-deploy, les noms de chunks lazy changent. Un onglet
 * deja ouvert tente de charger un ancien chunk → 404 → ChunkLoadError.
 */
const isChunkLoadError = (error: Error | null): boolean => {
  if (!error) return false;
  const msg = error.message || '';
  const name = error.name || '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Unable to preload')
  );
};

const RELOAD_FLAG_KEY = '__chunk_reload_attempt__';

/**
 * Tente un reload automatique une seule fois. Si on a déjà essayé dans la
 * session courante, on n'essaie plus → on affiche le fallback. Évite la boucle
 * infinie au cas où le problème ne serait pas un chunk périmé.
 */
const tryAutoReload = (): boolean => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) return false;
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);

    // Auto-reload pour les chunks périmés (déploiement pendant onglet ouvert)
    if (isChunkLoadError(error)) {
      if (tryAutoReload()) return;
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    } catch {
      /* noop */
    }
    window.location.reload();
  };

  handleGoHome = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    } catch {
      /* noop */
    }
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isChunk = isChunkLoadError(this.state.error);

    return (
      // data-nosnippet : si React échoue PENDANT le rendu de Googlebot, l'indexeur ne doit
      // pas prendre cet écran comme extrait de résultat. C'est arrivé : le 03/11/2026,
      // Google affichait encore « Quelque chose s'est mal passé. Vous pouvez recharger la
      // page… » sous la demande en mariage, le séminaire La Défense, la soirée
      // d'entreprise et l'article 40 ans — souvenir de la période où les snapshots
      // pointaient vers des bundles disparus. Le garde-fou ne touche qu'à l'extrait :
      // Google retombe alors sur la meta description, propre à chaque page et intacte
      // dans le <head> du snapshot. Pas de noindex ici : une erreur passagère
      // désindexerait une bonne page.
      <div
        data-nosnippet=""
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          backgroundColor: '#FAFAF8',
        }}
      >
        <div
          style={{
            maxWidth: '480px',
            width: '100%',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}
        >
          <div
            style={{
              fontSize: '40px',
              marginBottom: '16px',
            }}
            aria-hidden
          >
            ⚠️
          </div>
          <h1
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#1a1a1a',
              marginBottom: '12px',
            }}
          >
            {isChunk ? 'Mise à jour disponible' : 'Une erreur est survenue'}
          </h1>
          <p
            style={{
              fontSize: '14px',
              color: '#6b7280',
              lineHeight: 1.5,
              marginBottom: '24px',
            }}
          >
            {isChunk
              ? "Le site a été mis à jour pendant votre navigation. Rechargez la page pour récupérer la dernière version."
              : "Quelque chose s'est mal passé. Vous pouvez recharger la page ou retourner à l'accueil."}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={this.handleReload}
              style={{
                background: '#1a1a1a',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Recharger la page
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                background: 'white',
                color: '#1a1a1a',
                border: '1px solid #e5e7eb',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Retour à l'accueil
            </button>
          </div>
          {!isChunk && this.state.error && (
            <details
              style={{
                marginTop: '24px',
                textAlign: 'left',
                fontSize: '12px',
                color: '#9ca3af',
              }}
            >
              <summary style={{ cursor: 'pointer' }}>Détails techniques</summary>
              <pre
                style={{
                  marginTop: '8px',
                  padding: '8px',
                  background: '#f9fafb',
                  borderRadius: '4px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '11px',
                }}
              >
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

/**
 * Listener global pour les Promise rejections non catchées (typique
 * des lazy imports qui échouent : `import('./Page')` retourne une Promise).
 * Ces erreurs ne traversent PAS componentDidCatch — il faut les intercepter ici.
 */
export const installChunkErrorListener = (): void => {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason as Error | undefined;
    if (!error || !isChunkLoadError(error)) return;

    console.warn('[ChunkLoadError caught via unhandledrejection]', error.message);
    event.preventDefault();
    tryAutoReload();
  });

  // Vite émet `vite:preloadError` quand le préchargement d'un chunk lazy échoue
  // (cas LE PLUS fréquent après un redéploiement : l'ancien chunk n'existe plus).
  // Sans preventDefault, Vite relance l'erreur → page blanche. On recharge.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    console.warn('[vite:preloadError caught] → reload');
    tryAutoReload();
  });

  // Nettoyer le flag de reload une fois que la page est restée stable 10s
  // (signe que le reload a effectivement résolu le problème)
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    } catch {
      /* noop */
    }
  }, 10_000);
};
